/* eslint-disable react-refresh/only-export-components */
/**
 * ConfirmDialog — app-styled replacement for window.confirm().
 *
 * Usage:
 *   import { useConfirm, ConfirmProvider } from './ConfirmDialog';
 *
 *   // In App.jsx: wrap with <ConfirmProvider>
 *   // In any component:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete report?',
 *     message: 'This cannot be undone.',
 *     confirmText: 'Delete report',   // verb-specific per NN/G 2020+
 *     destructive: true,
 *   });
 *   if (!ok) return;
 *
 * A11y (hardened 2026-04-18 per W3C ARIA APG + WCAG 2.2 research):
 *   - role="alertdialog" (not "dialog") — confirmation dialogs interrupt
 *     the user and require a response; triggers more assertive SR
 *     announcement than plain "dialog". See
 *     https://www.w3.org/WAI/ARIA/apg/patterns/alertdialog/.
 *   - aria-labelledby + aria-describedby wire the title and message to
 *     the dialog landmark.
 *   - Destructive dialogs autoFocus Cancel (not the destructive action).
 *     2025 consensus: destructive modals should focus the safe option so
 *     Enter-key doesn't accidentally confirm. See
 *     https://uxpsychology.substack.com/p/how-to-design-better-destructive.
 *   - Focus returns to the triggering element on close (captured from
 *     document.activeElement at confirm-call time).
 *
 * Known limitations (deferred to a future focus-trap rewrite):
 *   - No Tab/Shift+Tab focus trap — a user tabbing repeatedly can still
 *     escape to the URL bar. Native <dialog> + showModal() gets this for
 *     free and is the planned upgrade path.
 *   - No `inert`/`aria-hidden` on background content.
 *
 * Matches Don's design principle: "No window.confirm() — use proper modals."
 * Closes Bug 31.
 */
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback for components outside provider — use native confirm.
    return async (opts) => window.confirm(opts?.message || opts?.title || 'Confirm?');
  }
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  // state: null | { title, message, confirmText, cancelText, destructive, resolve }

  // Capture the element that was focused when confirm() was called so we
  // can restore focus to it on close (WCAG 2.4.3 / APG alertdialog guidance).
  const previouslyFocusedRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    previouslyFocusedRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    return new Promise((resolve) => {
      setState({
        title: opts.title || 'Confirm',
        message: opts.message || '',
        confirmText: opts.confirmText || 'Confirm',
        cancelText: opts.cancelText || 'Cancel',
        destructive: !!opts.destructive,
        resolve,
      });
    });
  }, []);

  const restoreFocus = useCallback(() => {
    const prev = previouslyFocusedRef.current;
    previouslyFocusedRef.current = null;
    // queueMicrotask so React commits the dialog-close first; otherwise
    // focus returns to an element that's still inside the unmounting subtree.
    if (prev && typeof prev.focus === 'function') {
      queueMicrotask(() => { try { prev.focus(); } catch { /* element may be gone */ } });
    }
  }, []);

  const handleConfirm = useCallback(() => {
    state?.resolve(true);
    setState(null);
    restoreFocus();
  }, [state, restoreFocus]);

  const handleCancel = useCallback(() => {
    state?.resolve(false);
    setState(null);
    restoreFocus();
  }, [state, restoreFocus]);

  // Esc/Enter keyboard support.
  // For destructive dialogs, Enter is intentionally mapped to handleCancel
  // (the focused button). Users must click the red button explicitly.
  const handleKey = useCallback((e) => {
    if (!state) return;
    if (e.key === 'Escape') handleCancel();
    if (e.key === 'Enter' && !e.shiftKey) {
      // Follow the focused button. Destructive dialogs focus Cancel → Enter cancels.
      if (state.destructive) handleCancel();
      else handleConfirm();
    }
  }, [state, handleCancel, handleConfirm]);

  const messageId = 'confirm-message';

  // Replace `autoFocus` (jsx-a11y bans it: surprises screen-reader users) with
  // programmatic post-mount focus. Same UX outcome — destructive dialogs focus
  // Cancel, safe dialogs focus Confirm — without the SR-disorientation tax.
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);
  useEffect(() => {
    if (!state) return;
    // queueMicrotask so the dialog mounts before focus is moved.
    queueMicrotask(() => {
      const target = state.destructive ? cancelRef.current : confirmRef.current;
      try { target?.focus(); } catch { /* element may have unmounted */ }
    });
  }, [state]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          aria-describedby={state.message ? messageId : undefined}
          onClick={handleCancel}
          onKeyDown={handleKey}
          tabIndex={-1}
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
            animation: 'confirmFadeIn 0.12s ease-out',
          }}>
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: 12,
              padding: '20px 22px 18px',
              maxWidth: 440,
              width: '100%',
              boxShadow: '0 12px 48px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
              fontFamily: "'Inter', 'DM Sans', sans-serif",
              animation: 'confirmSlideIn 0.14s ease-out',
            }}>
            <h3 id="confirm-title" style={{
              margin: '0 0 8px',
              fontSize: 16,
              fontWeight: 600,
              color: '#1a1a2e',
              fontFamily: "'Inter', sans-serif",
            }}>{state.title}</h3>
            {state.message && (
              <p id={messageId} style={{
                margin: '0 0 18px',
                fontSize: 13,
                lineHeight: 1.5,
                color: '#4a5568',
                whiteSpace: 'pre-wrap',
              }}>{state.message}</p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                ref={cancelRef}
                onClick={handleCancel}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  border: '1px solid rgba(0,0,0,0.12)',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#4a5568',
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                }}>
                {state.cancelText}
              </button>
              <button
                ref={confirmRef}
                onClick={handleConfirm}
                style={{
                  padding: '8px 16px',
                  background: state.destructive ? '#dc2626' : '#1a1a2e',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                }}>
                {state.confirmText}
              </button>
            </div>
          </div>
          <style>{`
            @keyframes confirmFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes confirmSlideIn {
              from { transform: translateY(8px) scale(0.98); opacity: 0; }
              to { transform: translateY(0) scale(1); opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
